// Mirror of the SERVER-SIDE access rules (firestore.rules + storage.rules),
// expressed per role, used by the live permission probes. Each probe attempts
// a real operation and asserts the server's answer (allowed / denied) matches
// this table — so a probe passes when access is GRANTED as expected AND when
// it is DENIED as expected. If a rule is intentionally changed, update the
// matching entry here (same discipline as config/rolePermissionExpectations).

import { normalizeUserGovernance } from '@/utils/userGovernance'
import { canReadAuditLogs, isDistrictWideReader } from '@/utils/auditLogScope'
import { canManageMeetings } from '@/utils/meetingPermissions'

// firestore.rules isAnyAdmin()
const FS_ANY_ADMIN = [
  'admin',
  'school_admin',
  'admin_504',
  'sped_chair',
  'sped_chair_all',
  'district_admin'
]

// firestore.rules isSuperAdmin()
const FS_SUPER_ADMIN = ['admin', 'school_admin', 'admin_504', 'district_admin']

// firestore.rules hasFullReadAccess()
//   isAnyAdmin() || isStaffViewOrEdit() || isTeacherFull() || isCaseManagerFull() || isSchoolPsych()
const FS_FULL_READ = [
  ...FS_ANY_ADMIN,
  'staff_view',
  'staff_edit',
  'staff_edit_docs',
  'teacher_full',
  'case_manager_full',
  'school_psych'
]

// storage.rules isAnyAdmin() — kept in sync with firestore.rules (includes district_admin).
const ST_ANY_ADMIN = [
  'admin',
  'school_admin',
  'admin_504',
  'sped_chair',
  'sped_chair_all',
  'district_admin'
]

const CASELOAD_WRITERS = ['case_manager', 'case_manager_full', 'speech_case_manager']

/**
 * Mirrors firestore.rules canCreateUsersByPolicy — not the client helper,
 * which currently lists district_coordinator in every governance mode.
 */
export function expectedUserCreate(role, governance) {
  const g = normalizeUserGovernance(governance || {})
  if (role === 'admin' || role === 'district_admin') return true
  if (role === 'district_coordinator') return g.whoCanCreateUsers === 'district_only'
  if (role === 'school_admin' || role === 'admin_504') {
    return (
      g.whoCanCreateUsers === 'school_admins' ||
      g.whoCanCreateUsers === 'school_admins_and_sped_chairs'
    )
  }
  if (role === 'sped_chair' || role === 'sped_chair_all') {
    return g.whoCanCreateUsers === 'school_admins_and_sped_chairs'
  }
  return false
}

/**
 * School-scoped list expectation. `null` means the probe cannot resolve
 * (school-tier super-admin with no active school) — record blocked, not pass.
 */
export function expectedAuditLogRead(role, { schoolId = null, tokenSchoolIds = null } = {}) {
  if (!canReadAuditLogs(role)) return false
  if (isDistrictWideReader(role, tokenSchoolIds)) return true
  if (!schoolId) return null
  return Array.isArray(tokenSchoolIds) && tokenSchoolIds.includes(schoolId)
}

/**
 * Mirrors firestore.rules canWriteSchoolMeetingDefaults. `admin` and
 * `district_admin` write any school doc (including a fake smoke id);
 * school-tier admins write only a docId that is on their JWT. Leftover
 * `global` is never writable.
 */
export function expectedMeetingDefaultsWrite(role, { docId, tokenSchoolIds = null } = {}) {
  if (!docId || docId === 'global') return false
  if (!FS_ANY_ADMIN.includes(role)) return false
  if (role === 'admin' || role === 'district_admin') return true
  return Array.isArray(tokenSchoolIds) && tokenSchoolIds.includes(docId)
}

/**
 * Mirrors genEdHours read: hasFullReadAccess / isAnyAdmin / any CM /
 * speech CM, or teacherId == uid (isOwn).
 */
export function expectedGenEdHoursRead(role, { isOwn = false } = {}) {
  if (isOwn) return true
  return FS_FULL_READ.includes(role) || role === 'case_manager' || role === 'speech_case_manager'
}

/** firestore.rules hasTestingAccess — full-read roles, or users/{uid}.testingAccess. */
export function expectedHasTestingAccess(role, { testingAccess = false } = {}) {
  return FS_FULL_READ.includes(role) || !!testingAccess
}

/**
 * administersSchool: district_admin / district_coordinator any campus;
 * admin / school_admin only when the doc id is on the JWT.
 */
export function expectedAdministersSchool(role, { schoolId, tokenSchoolIds = null } = {}) {
  if (!schoolId) return false
  if (role === 'district_admin' || role === 'district_coordinator') return true
  if (
    (role === 'admin' || role === 'school_admin') &&
    Array.isArray(tokenSchoolIds) &&
    tokenSchoolIds.includes(schoolId)
  ) {
    return true
  }
  return false
}

/**
 * studentPrivate write. A missing parent is always denied in practice:
 * parentStudent() does get(students/{id}).data, which fails the rule when
 * the student doc does not exist — the `doc == null && canCreateStudentLike()`
 * branch in firestore.rules never evaluates. Parent exists: super-admin /
 * staff-edit / related CM / school_psych. SPED chairs are denied even when
 * they created the student (they are not in that list).
 */
export function expectedStudentPrivateWrite(
  role,
  { parentExists = false, isRelated = false } = {}
) {
  if (!parentExists) return false
  if (FS_SUPER_ADMIN.includes(role)) return true
  if (role === 'staff_edit' || role === 'staff_edit_docs') return true
  if (CASELOAD_WRITERS.includes(role) && isRelated) return true
  return role === 'school_psych'
}

export function expectedStudentPrivateRead(
  role,
  { parentExists = false, canReadStudent = false } = {}
) {
  if (!parentExists) return false
  if (role === 'paraeducator' || role === 'district_coordinator') return false
  return !!canReadStudent
}

export const ISSUE_REPORT_OWNER_EMAILS = [
  'ryan@casemanagevue.com',
  'admin@casemanagevue.com',
  'rypdavis@gmail.com',
  'casemanagevue@gmail.com'
]

export function expectedIssueReportRead(email) {
  return ISSUE_REPORT_OWNER_EMAILS.includes(String(email || '').toLowerCase())
}

const TESTING_CALENDAR_COORDINATORS = [
  'admin',
  'district_admin',
  'school_admin',
  'admin_504',
  'sped_chair',
  'sped_chair_all',
  'staff_view',
  'staff_edit',
  'staff_edit_docs',
  'school_psych'
]

export function expectedTestingCalendarWrite(role, { isOwnTeacher = false } = {}) {
  return !!isOwnTeacher || TESTING_CALENDAR_COORDINATORS.includes(role)
}

/**
 * Mirrors inScope(request.resource.data) for a smoke doc stamped with the
 * selected school. district_admin and UNstamped admin are district-wide;
 * district_coordinator is NOT (operations role, inScope has no branch for it).
 * Everyone else needs the selected school on the JWT. The allSchools CM claim
 * is not modeled — the demo rotator never sets it.
 */
export function expectedInScopeCreate({ role, tokenSchoolIds = null, schoolId = null } = {}) {
  if (role === 'district_admin') return true
  if (role === 'admin' && !Array.isArray(tokenSchoolIds)) return true
  return Array.isArray(tokenSchoolIds) && !!schoolId && tokenSchoolIds.includes(schoolId)
}

export const RULE_EXPECTATIONS = {
  // students: allow create if isAnyAdmin || isCaseManagerFull || isSchoolPsych.
  // 2026-08-29: plain/speech CMs lost create — limited read means they cannot
  // check an SSID for duplicates. Keep aligned with STUDENT_CREATE_ROLES in
  // src/utils/studentEvaluation.js.
  studentCreate: (role) =>
    FS_ANY_ADMIN.includes(role) || ['case_manager_full', 'school_psych'].includes(role),

  // students: allow delete if isSuperAdmin || isAnySpedChair.
  // school_psych may also delete an Initial-pending shell (outcome apply);
  // this expectation is the generic (plan-in-force) delete used by studentsCrud.
  studentDelete: (role) =>
    FS_SUPER_ADMIN.includes(role) || role === 'sped_chair' || role === 'sped_chair_all',

  // School-only query: broad staff roles must now filter staffAccess. Only
  // the referral team may query across both restricted and standard students.
  studentListReadable: (role) => FS_ANY_ADMIN.includes(role) || role === 'school_psych',

  // users: allow list if hasValidRole && userInScopeForList(). The smoke query
  // is school-scoped (array-contains), which any valid role may run.
  usersListReadable: () => true,

  // users: single-doc read — any valid role (get rule).
  userRead: () => true,

  // users: update a NON-owned doc, non-privileged field → isAnyAdmin path.
  userUpdate: (role) => FS_ANY_ADMIN.includes(role),

  // app_settings/{document}: district-tier write (admin | district_admin)
  appSettingsWrite: (role) => role === 'admin' || role === 'district_admin',

  // auditLogs: isSuperAdmin && auditLogListInScope. The live probe uses a
  // schoolId filter when a school is selected so school-tier tokens get a
  // true/false instead of 'depends'.
  auditLogRead: (role, ctx = {}) => expectedAuditLogRead(role, ctx),

  // feedbackTemplates: allow create if isAnyAdmin || isAnyCaseManager || speech CM || school_psych
  feedbackTemplateCreate: (role) =>
    FS_ANY_ADMIN.includes(role) || [...CASELOAD_WRITERS, 'school_psych'].includes(role),

  // feedbackInstances: allow create if isAnyAdmin || isAnyCaseManager || speech CM || school_psych
  feedbackInstanceCreate: (role) =>
    FS_ANY_ADMIN.includes(role) || [...CASELOAD_WRITERS, 'school_psych'].includes(role),

  // feedbackResponses update after Close now: isSuperAdmin() is the first
  // branch and does not look at responderClosedAt. Everyone else is frozen
  // by canTeacherEditSubmittedResponse.
  feedbackEditAfterClose: (role) => FS_SUPER_ADMIN.includes(role),

  // studentEvaluations: the role must be a creator AND the evalType must match
  // the strict split — admin_504 → 504 only; sped chairs / psych → IEP only;
  // admin / district_admin / school_admin → either. 2026-08-30: CMs of every
  // variant lost create — they work assigned evals, they do not start them.
  // Keep aligned with allowedEvalTypesForRole in src/utils/studentEvaluation.js
  studentEvaluationCreate: (role, ctx = {}) => {
    const evalType = ctx.evalType || 'IEP'
    const isCreator = FS_ANY_ADMIN.includes(role) || role === 'school_psych'
    if (!isCreator) return false
    if (['admin', 'district_admin', 'school_admin'].includes(role)) return true
    if (role === 'admin_504') return evalType === '504'
    return evalType === 'IEP'
  },

  // studentEvaluations delete: same set as create (chairs / psych / 504
  // coordinator / admin tier). Not the assigned plain CM.
  studentEvaluationDelete: (role) => FS_ANY_ADMIN.includes(role) || role === 'school_psych',

  // users: create policy — mirrors canCreateUsersByPolicy + app_settings
  // userGovernance. Pass { governance } from app_settings/global.
  userCreate: (role, ctx = {}) => expectedUserCreate(role, ctx.governance),

  // users: delete = super-admin tier
  userDelete: (role) => FS_SUPER_ADMIN.includes(role),

  // storage students/{id}/... write: ALL student-file paths (general/,
  // sensitive/, the {filePath=**} fallback, and encrypted-pdfs/) now share
  // canWriteStudentFiles: admins + staff_edit + the student's OWN case
  // manager. staffIds membership and extended access are read-level only, and
  // a case manager with no ownership of the student cannot write.
  // 2026-08-30: OR the open-eval owner (fillable shell — documents are part
  // of the eval CM's narrowed surface). Mirrors storage.rules canWriteStudentFiles.
  storageStudentWrite: (role, { isCaseManagerOfStudent, isEvalCaseManagerOfStudent } = {}) =>
    ST_ANY_ADMIN.includes(role) ||
    ['staff_edit', 'staff_edit_docs'].includes(role) ||
    (CASELOAD_WRITERS.includes(role) && (!!isCaseManagerOfStudent || !!isEvalCaseManagerOfStudent)),

  // storage read: all student-file paths share canReadStudentFiles — full-read
  // roles, staffIds members, the student's own case manager, or extended
  // access. general/ is no longer world-readable to every valid role.
  storageStudentRead: (role, { inStaffIds, isCaseManagerOfStudent } = {}) =>
    FS_FULL_READ.includes(role) ||
    !!inStaffIds ||
    (CASELOAD_WRITERS.includes(role) && !!isCaseManagerOfStudent),

  // emailConfig: read isAnyAdmin; write is canWriteDistrictSettings.
  emailConfigRead: (role) => FS_ANY_ADMIN.includes(role),
  emailConfigWrite: (role) => role === 'admin' || role === 'district_admin',

  // aideSchedules create/delete of a single-campus smoke doc: isAnyAdmin + inScope.
  aideScheduleWrite: (role) => FS_ANY_ADMIN.includes(role),

  // extendedAccess: isAnyAdmin, or a caseload writer who owns the student.
  extendedAccessWrite: (role, { isManagerOfStudent } = {}) =>
    FS_ANY_ADMIN.includes(role) || (CASELOAD_WRITERS.includes(role) && !!isManagerOfStudent),

  // extendedAccessStudents: Cloud Functions only.
  extendedAccessStudentsWrite: () => false,

  // meetings: canEditMeetingFor — CM-of-record (or eval CM) for assignable
  // roles, plus admin_504 for every 504 student. A missing student doc always
  // denies because the rule get()s students/{id}.
  meetingWrite: (role, ctx = {}) => {
    if (!ctx.studentExists) return false
    return canManageMeetings(
      { uid: ctx.uid, role },
      {
        app: {
          studentData: {
            caseManagerId: ctx.caseManagerId,
            plan: ctx.plan || 'IEP'
          },
          evalCaseManagerId: ctx.evalCaseManagerId || ''
        }
      }
    )
  },

  // meetingDefaults: isAnyAdmin + not leftover `global` + (district-tier
  // writer OR schoolId on the JWT). Create/update also require schoolId == docId.
  meetingDefaultsWrite: (role, ctx = {}) => expectedMeetingDefaultsWrite(role, ctx),
  meetingDefaultsRead: () => true,

  // genEdHours: Cloud Functions only for write. Read is full-read / admin /
  // any CM / speech CM, or the teacher's own doc.
  genEdHoursWrite: () => false,
  genEdHoursRead: (role, ctx = {}) => expectedGenEdHoursRead(role, ctx),

  // Testing writes also require inScope(request.resource.data) — the
  // scenarios AND these role predicates with inScopeCreate.
  testingAccommodationsWrite: () => false,
  testingAttendanceWrite: (role, ctx = {}) =>
    expectedHasTestingAccess(role, ctx) || FS_ANY_ADMIN.includes(role),
  testingSessionNotesWrite: (role, ctx = {}) =>
    expectedHasTestingAccess(role, ctx) || FS_ANY_ADMIN.includes(role),
  testingCalendarWrite: (role, ctx = {}) => expectedTestingCalendarWrite(role, ctx),

  inScopeCreate: (role, ctx = {}) => expectedInScopeCreate({ role, ...ctx }),

  schoolCreate: () => false, // licensed createSchool callable only
  schoolDelete: () => false,
  schoolPrivateWrite: (role, ctx = {}) => expectedAdministersSchool(role, ctx),
  schoolStatsWrite: () => false,
  schoolStatsRead: (role) =>
    role === 'district_admin' || role === 'district_coordinator' || FS_ANY_ADMIN.includes(role),

  studentPrivateWrite: (role, ctx = {}) => expectedStudentPrivateWrite(role, ctx),
  studentPrivateRead: (role, ctx = {}) => expectedStudentPrivateRead(role, ctx),
  studentPrivateDelete: (role, { parentExists = false, parentInScope = false } = {}) =>
    FS_SUPER_ADMIN.includes(role) && parentExists && parentInScope,
  studentPrivateList: () => false,

  refDocumentWrite: (role) => FS_ANY_ADMIN.includes(role),
  refDocumentStorageWrite: (role) => ST_ANY_ADMIN.includes(role),
  refDocumentStorageRead: () => true,

  backupWrite: (role) => role === 'district_admin',
  issueReportCreate: () => true,
  issueReportRead: (role, ctx = {}) => expectedIssueReportRead(ctx.email),
  issueReportDelete: () => false,
  pdfMetadataWrite: () => true,
  pdfMetadataDelete: (role) => FS_ANY_ADMIN.includes(role),
  pdfAccessLogCreate: () => false, // only recordAuditEvent writes browser reports
  pdfAccessLogDelete: () => false,
  pdfAccessLogRead: (role) =>
    FS_ANY_ADMIN.includes(role) || CASELOAD_WRITERS.includes(role) || role === 'school_psych'
}

/**
 * Attempt an operation and report whether the server ALLOWED it. Only
 * permission errors are swallowed (turned into `allowed:false`); any other
 * error (network, bad data, assertion) propagates so the step fails loudly.
 */
export async function attempt(fn) {
  try {
    await fn()
    return { allowed: true }
  } catch (err) {
    const denied =
      err?.code === 'permission-denied' ||
      err?.code === 'storage/unauthorized' ||
      err?.code === 'storage/unauthenticated'
    if (!denied) throw err
    return { allowed: false, code: err.code }
  }
}

/**
 * Compare the server's actual answer to the expectation and return a
 * human-readable detail string, throwing on a mismatch.
 * - expected true  + denied  → throw (role should be allowed)
 * - expected false + allowed → throw SECURITY (role should be denied)
 * - 'depends'               → either outcome is acceptable
 */
export function accessDetail(expected, allowed) {
  if (expected === 'depends') {
    return allowed ? 'allowed (governance-dependent)' : 'denied (governance-dependent)'
  }
  if (expected && !allowed) {
    throw new Error('Denied by server but this role should be allowed')
  }
  if (!expected && allowed) {
    throw new Error('SECURITY: operation SUCCEEDED but rules should deny it for this role')
  }
  return expected ? 'allowed, as expected' : 'denied, as expected'
}

/** Short label for a step title. */
export function fmtExpected(expected) {
  if (expected === 'depends') return 'governance-dependent'
  return expected ? 'allowed' : 'denied'
}

/**
 * Convenience wrapper for standalone allow/deny probes (no dependent steps).
 * Returns the detail string; throws on mismatch. Runs `onUnexpectedSuccess`
 * (e.g. cleanup) before throwing the SECURITY error.
 */
export async function probe(expected, fn, { onUnexpectedSuccess } = {}) {
  const { allowed } = await attempt(fn)
  if (expected !== 'depends' && !expected && allowed && onUnexpectedSuccess) {
    await onUnexpectedSuccess()
  }
  return accessDetail(expected, allowed)
}
